// An agent's proposal as the engine published it (`actions.data.proposal`, UI_DATA §8.1): for each item, what the record holds
// now against what it would become, with the item's sources. Agent-authored content is violet with the agent chip; a reply to
// the client names the client's message (teal) but never copies the client's words into the action.
import type { Proposal } from "../data/types";
import { titleCase } from "../lib/format";
import { DiffView } from "./DiffView";
import { AgentChip } from "./primitives";
import { SourceList } from "./SourceChip";

const KIND: Record<string, string> = {
  answers: "Proposed answers", procedure_results: "Proposed procedure results", thread_message: "Drafted message to the client",
  repair_plan: "Explained repair plan", learning: "Candidate learning",
};
const asRecord = (v: unknown): Record<string, unknown> | null => (v === null || v === undefined ? null : typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : { value: v });
const show = (v: unknown) => (typeof v === "object" && v !== null ? JSON.stringify(v) : String(v));

export function ProposalView({ proposal }: { proposal: Proposal }) {
  return (
    <section aria-label="Agent's proposal" className="space-y-3 rounded-md border border-agent-line p-3">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <AgentChip name={proposal.agent.join(", ") || undefined} />
        <span className="font-medium">{KIND[proposal.kind] ?? titleCase(proposal.kind)}</span>
        {proposal.by.length > 0 && <span className="text-xs text-muted">for {proposal.by.join(", ")} (owner of record)</span>}
      </p>
      {proposal.note && <p className="text-xs text-muted">{proposal.note}</p>}
      {proposal.items.length === 0 ? <p className="text-sm text-muted">The engine published no items for this proposal.</p> : (
        <ol className="space-y-3" aria-label="Proposed items">
          {proposal.items.map((it, i) => {
            const reply = asRecord(it.extra.in_reply_to);
            const facts = Object.entries(it.extra).filter(([k]) => k !== "in_reply_to");
            return (
              <li key={`${it.ref}-${i}`} className="space-y-1.5">
                <p className="text-sm"><span className="font-medium">{it.label}</span>{it.ref && it.ref !== it.label && <span className="ml-2 font-mono text-xs text-muted">{it.ref}</span>}</p>
                {reply && (
                  <p className="inline-flex flex-wrap items-center gap-1 rounded border border-client-line bg-client-bg px-1.5 py-0.5 text-xs text-client">
                    In reply to the {reply.direction === "client" ? "client's" : String(reply.direction ?? "")} message {String(reply.id ?? "")}
                  </p>
                )}
                <DiffView before={asRecord(it.before)} after={asRecord(it.after)} />
                {facts.length > 0 && (
                  <dl className="grid grid-cols-[minmax(6rem,max-content)_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
                    {facts.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted">{titleCase(k)}</dt><dd className="min-w-0 break-words">{show(v)}</dd></div>)}
                  </dl>
                )}
                {it.sources.length > 0 && <p className="flex flex-wrap items-center gap-1 text-xs"><span className="text-muted">Sources</span><SourceList sources={it.sources} /></p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
